import { useCallback, useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import type { OnDataOptions } from '@apollo/client';
import type {
  DownloadJobProgressPayload,
  ImportItemError,
  UxpTransferState,
} from '@frameio/graphql-client/generated';
import {
  type ImportEventsSubscription,
  useImportEventsSubscription,
} from '@frameio/shared/integrations/gql';
import { formatFileSizeInMB } from '@frameio/utils/formatters';
import { useDatadogLogger } from '../../shared/hooks/useDatadogLogger';

type ImportProgressContextType = {
  items: Record<
    string,
    {
      progress: number;
      state: UxpTransferState;
    }
  >;
  progress: number;
  hasProgress: boolean;
  hasCompleted: boolean;
};

const ImportProgressContext = createContext<ImportProgressContextType>({
  items: {},
  progress: 0,
  hasProgress: false,
  hasCompleted: false,
});

export const ImportProgressProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const datadogLogger = useDatadogLogger();

  const logToDatadog = useCallback(
    (
      message: string,
      payload?: DownloadJobProgressPayload,
      errors?: ImportItemError[]
    ) => {
      const trackData = {
        source: 'PrUXP',
        transfer_job_id: payload?.id,
        total_file_size: payload
          ? formatFileSizeInMB(payload.bytesTotal)
          : undefined,
        errors: errors?.map((error) => error.message),
      };

      datadogLogger.info(message, {
        uxpDownloads: trackData,
      });
    },
    [datadogLogger]
  );

  const [importProgressItems, setImportProgressItems] = useState<
    Record<
      string,
      {
        progress: number;
        state: UxpTransferState;
      }
    >
  >({});

  const onDownloadJobProgressData = useCallback(
    (payload: DownloadJobProgressPayload) => {
      if (!importProgressItems[payload.id]) {
        logToDatadog('integrations-download-started', payload);
      }

      const currState = importProgressItems[payload.id]?.state;

      ['completed', 'failed', 'canceled'].forEach((payloadState) => {
        if (payload.state === payloadState && currState !== payloadState) {
          logToDatadog(`integrations-download-${payloadState}`, payload);
        }
      });

      setImportProgressItems((prev) => ({
        ...prev,
        [payload.id]: {
          progress: Math.min(payload.percentage * 100, 100),
          state: payload.state,
        },
      }));
    },
    [importProgressItems, logToDatadog]
  );
  const onImportAssetProgressData = useCallback(
    (options: OnDataOptions<ImportEventsSubscription>) => {
      const importProgress = options.data?.data?.importEvents;
      if (!importProgress) return;

      switch (importProgress.__typename) {
        case 'DownloadJobProgressPayload':
          onDownloadJobProgressData(importProgress);
          break;
        case 'ImportCompletePayload':
          logToDatadog(
            'integrations-import-completed',
            undefined,
            importProgress.errors
          );
          break;
        case 'ImportCanceledPayload':
          logToDatadog('integrations-import-canceled');
          break;
        default:
          break;
      }
    },
    [onDownloadJobProgressData, logToDatadog]
  );

  useImportEventsSubscription({
    context: { resolveWithUXP: true },
    onData: onImportAssetProgressData,
  });

  const value = useMemo(() => {
    const hasProgress = Object.values(importProgressItems).length > 0;
    const hasCompleted =
      hasProgress &&
      Object.values(importProgressItems).every(
        ({ progress }) => progress === 100
      );

    const progress = Object.values(importProgressItems).reduce(
      (acc, itemProgress) => acc + (itemProgress.progress || 0),
      0
    );
    return { items: importProgressItems, progress, hasProgress, hasCompleted };
  }, [importProgressItems]);

  return (
    <ImportProgressContext.Provider value={value}>
      {children}
    </ImportProgressContext.Provider>
  );
};

export const useImportProgress = () => {
  const { items, progress, hasProgress, hasCompleted } = useContextSelector(
    ImportProgressContext,
    (context) => context
  );
  return { items, progress, hasProgress, hasCompleted };
};
